/**
 * Motor de despiece de fabricación.
 *
 * Un producto del taller no es una receta plana de insumos: es un despiece. Y hay
 * tres cosas que un modelo plano no captura y que sí determinan el costo real:
 *
 *  1. **La placa no se compra por metro cuadrado, se compra por placa.** Lo que
 *     decide cuántas placas hacen falta no es el área de las piezas, es cuántas
 *     caben — el anidado. Por eso el aprovechamiento es un parámetro explícito y
 *     el consumo se redondea hacia arriba a placas enteras.
 *  2. **El corte se cobra por trayectoria, no por área.** Una pieza calada con
 *     mucho detalle tiene el mismo material que una lisa y cuesta el triple de
 *     máquina. Por eso los metros de corte salen del perímetro, no del área.
 *  3. **El minuto de máquina y el minuto de banco no valen lo mismo.** Y la
 *     máquina es el recurso escaso del taller, no el dinero.
 *
 * Todo lo que sale de aquí es **costo**. Las utilidades, el IVA y la protección de
 * rentabilidad las aplica `calculo.js` encima, igual que a cualquier otro producto.
 *
 * VALORES DE MUESTRA: los costos de material, las velocidades de corte y los
 * minutos de proceso son aproximaciones razonables para arrancar, NO datos del
 * taller. Están puestos para que la interfaz de captura exista y se pueda editar.
 * Cada uno se corrige desde la pantalla de Configuración sin tocar código.
 */

/** Placas y perfiles, con su medida comercial y su costo por unidad completa. */
export const MATERIALES_BASE = {
  MDF18:     { nombre: 'MDF 18 mm',            largo: 2440, ancho: 1220, costo: 780,  corte: 'routerCNC' },
  MDF6:      { nombre: 'MDF 6 mm',             largo: 2440, ancho: 1220, costo: 320,  corte: 'routerCNC' },
  TRIPLAY15: { nombre: 'Triplay 15 mm',        largo: 2440, ancho: 1220, costo: 1080, corte: 'routerCNC' },
  LAM16:     { nombre: 'Lámina cal. 16',       largo: 2440, ancho: 1220, costo: 1450, corte: 'laserFibra' },
  LAM14:     { nombre: 'Lámina cal. 14',       largo: 2440, ancho: 1220, costo: 1890, corte: 'laserFibra' },
  ACRILICO3: { nombre: 'Acrílico 3 mm',        largo: 1830, ancho: 1220, costo: 890,  corte: 'laserMDF' }
}

/**
 * Procesos, con su costo por minuto y su velocidad.
 * El costo del minuto incluye máquina más operador: por eso el router cuesta el
 * triple que el banco de armado.
 */
export const PROCESOS_BASE = {
  routerCNC:  { nombre: 'Router CNC',              costoMinuto: 9.5,  metrosPorMinuto: 3.2 },
  laserFibra: { nombre: 'Láser de fibra',          costoMinuto: 14.0, metrosPorMinuto: 2.1 },
  laserMDF:   { nombre: 'Láser MDF y acrílico',    costoMinuto: 7.5,  metrosPorMinuto: 1.8 },
  canteado:   { nombre: 'Canteado',                costoMinuto: 4.5,  metrosPorMinuto: 2.5 },
  doblez:     { nombre: 'Dobladora',               costoMinuto: 8.0,  porDoblez: 0.6 },
  soldadura:  { nombre: 'Soldadura',               costoMinuto: 6.0 },
  armado:     { nombre: 'Armado en banco',         costoMinuto: 3.5 },
  pintura:    { nombre: 'Pintura electrostática',  costoMinuto: 5.0 },
  sublimado:  { nombre: 'Sublimación',             costoMinuto: 4.0 },
  empaque:    { nombre: 'Empaque',                 costoMinuto: 2.5 }
}

/** Herrajes y consumibles de fabricación, por pieza o por metro. */
export const HERRAJES_BASE = {
  'HER-CORREDERA45': { nombre: 'Corredera de 45 cm, par', costo: 118, unidad: 'juego' },
  'HER-BISAGRA':     { nombre: 'Bisagra cazoleta',        costo: 34,  unidad: 'pieza' },
  'HER-TIRADOR':     { nombre: 'Tirador',                 costo: 62,  unidad: 'pieza' },
  'HER-TORNILLO':    { nombre: 'Tornillo 4 × 30',         costo: 1.2, unidad: 'pieza' },
  'HER-TARUGO':      { nombre: 'Tarugo de madera',        costo: 0.8, unidad: 'pieza' },
  'HER-CANTO':       { nombre: 'Canto de PVC',            costo: 9.5, unidad: 'metro' },
  'HER-NIVELADOR':   { nombre: 'Nivelador de mueble',     costo: 14,  unidad: 'pieza' },
  'HER-TAQUETE':     { nombre: 'Taquete y pija de fijación', costo: 4.5, unidad: 'pieza' },
  'HER-PINTURA':     { nombre: 'Polvo de pintura, por m²', costo: 46,  unidad: 'm2' }
}

/** Aprovechamiento por defecto al anidar piezas en una placa. */
export const APROVECHAMIENTO_BASE = 0.78

/* ============================ Motor ============================ */

const areaM2 = (largo, ancho) => (largo * ancho) / 1e6
const perimetroM = (largo, ancho) => (2 * (largo + ancho)) / 1000

/**
 * Calcula el despiece completo de un producto fabricado.
 *
 * @param despiece  { piezas[], herrajes[], procesos[], aprovechamiento }
 * @param cfg       { materiales, procesos, herrajes, aprovechamiento }
 * @returns desglose de material, procesos y herrajes, todo a COSTO
 */
export function calcularFabricacion(despiece, cfg = {}, cantidad = 1) {
  const MAT = { ...MATERIALES_BASE, ...(cfg.materiales || {}) }
  const PRO = { ...PROCESOS_BASE, ...(cfg.procesos || {}) }
  const HER = { ...HERRAJES_BASE, ...(cfg.herrajes || {}) }
  const aprov = despiece.aprovechamiento || cfg.aprovechamiento || APROVECHAMIENTO_BASE
  const q = Math.max(1, cantidad || 1)

  /* --- Material: se agrupa por placa y se redondea a placas enteras --- */
  const porMaterial = new Map()
  for (const p of despiece.piezas || []) {
    const m = MAT[p.material]
    if (!m) continue
    if (!porMaterial.has(p.material))
      porMaterial.set(p.material, { clave: p.material, nombre: m.nombre, area: 0, corteM: 0, cantoM: 0, piezas: 0 })
    const acc = porMaterial.get(p.material)
    const cant = p.cant || 1
    acc.area += areaM2(p.largo, p.ancho) * cant
    acc.piezas += cant
    // Perímetro completo, más el calado interior si la pieza lo lleva
    acc.corteM += (perimetroM(p.largo, p.ancho) + (p.corteExtraM || 0)) * cant
    // Canto solo en los lados que quedan a la vista
    acc.cantoM += ((p.cantoLargo || 0) * p.largo + (p.cantoAncho || 0) * p.ancho) / 1000 * cant
  }

  const materiales = [...porMaterial.values()].map(x => {
    const m = MAT[x.clave]
    const areaPlaca = areaM2(m.largo, m.ancho)
    const areaUtil = areaPlaca * aprov

    // El redondeo a placa entera ocurre al nivel del PEDIDO, no de la unidad: seis
    // repisas se anidan en la misma placa y no consumen seis. Por eso el área se
    // multiplica por la cantidad ANTES de redondear, y el costo unitario se obtiene
    // dividiendo al final.
    //
    // Ése es el efecto del anidado, y aquí queda medible: al subir la cantidad, el
    // costo por pieza baja sin que cambie nada más. Es el argumento económico de
    // fabricar por lotes, hecho número.
    //
    // Un producto que se vende por metro cuadrado —celosía, lambrín— reparte la
    // placa siempre, así que se prorratea y no se redondea.
    const areaPedido = x.area * q
    const placasPedido = despiece.prorratear
      ? areaPedido / areaUtil
      : Math.ceil(areaPedido / areaUtil)
    const placas = placasPedido / q   // parte que le toca a una unidad

    return {
      ...x,
      areaPlaca,
      placas,
      placasPedido,
      prorrateado: !!despiece.prorratear,
      costoPlaca: m.costo,
      costo: placas * m.costo,
      desperdicioPct: despiece.prorratear
        ? (1 - aprov) * 100
        : (placasPedido > 0 ? (1 - areaPedido / (placasPedido * areaPlaca)) * 100 : 0),
      procesoCorte: m.corte
    }
  })

  /* --- Procesos: los de corte y canteado se derivan; el resto se declara --- */
  const minutos = new Map()
  const sumar = (clave, min) => minutos.set(clave, (minutos.get(clave) || 0) + min)

  for (const x of materiales) {
    const pr = PRO[x.procesoCorte]
    if (pr?.metrosPorMinuto) sumar(x.procesoCorte, x.corteM / pr.metrosPorMinuto)
    if (x.cantoM > 0 && PRO.canteado?.metrosPorMinuto)
      sumar('canteado', x.cantoM / PRO.canteado.metrosPorMinuto)
  }
  for (const p of despiece.procesos || []) sumar(p.proceso, p.minutos || 0)
  if (despiece.dobleces && PRO.doblez?.porDoblez)
    sumar('doblez', despiece.dobleces * PRO.doblez.porDoblez)

  const procesos = [...minutos.entries()]
    .filter(([, min]) => min > 0)
    .map(([clave, min]) => ({
      clave,
      nombre: PRO[clave]?.nombre || clave,
      minutos: Math.round(min * 10) / 10,
      costoMinuto: PRO[clave]?.costoMinuto || 0,
      costo: min * (PRO[clave]?.costoMinuto || 0)
    }))
    .sort((a, b) => b.costo - a.costo)

  /* --- Herrajes: los declarados, más el canto derivado del despiece --- */
  const listaHerrajes = [...(despiece.herrajes || [])]
  const cantoTotal = materiales.reduce((a, x) => a + x.cantoM, 0)
  if (cantoTotal > 0) listaHerrajes.push({ sku: 'HER-CANTO', cant: Math.ceil(cantoTotal * 1.1) })

  const herrajes = listaHerrajes.map(h => {
    const d = HER[h.sku] || { nombre: h.sku, costo: 0, unidad: 'pieza' }
    return { sku: h.sku, nombre: d.nombre, unidad: d.unidad, cant: h.cant, costoUnit: d.costo, costo: d.costo * h.cant }
  })

  const cMaterial = materiales.reduce((a, x) => a + x.costo, 0)
  const cProcesos = procesos.reduce((a, x) => a + x.costo, 0)
  const cHerrajes = herrajes.reduce((a, x) => a + x.costo, 0)
  const minutosTotal = procesos.reduce((a, x) => a + x.minutos, 0)
  const minutosMaquina = procesos
    .filter(p => ['routerCNC', 'laserFibra', 'laserMDF', 'canteado', 'doblez'].includes(p.clave))
    .reduce((a, x) => a + x.minutos, 0)

  return {
    materiales, procesos, herrajes,
    costoMaterial: cMaterial,
    costoProcesos: cProcesos,
    costoHerrajes: cHerrajes,
    costoTotal: cMaterial + cProcesos + cHerrajes,
    minutosTotal: Math.round(minutosTotal * 10) / 10,
    /** El recurso escaso del taller, y el que hay que vigilar al programar la semana. */
    minutosMaquina: Math.round(minutosMaquina * 10) / 10,
    aprovechamiento: aprov,
    cantidad: q
  }
}

/**
 * Cuánto baja el costo unitario al fabricar por lote, por efecto del anidado.
 * Es el argumento económico de la línea modular, hecho número.
 */
export function ahorroPorLote(despiece, cfg = {}, cantidad = 6) {
  const uno = calcularFabricacion(despiece, cfg, 1)
  const lote = calcularFabricacion(despiece, cfg, cantidad)
  const baja = uno.costoTotal > 0 ? (1 - lote.costoTotal / uno.costoTotal) * 100 : 0
  return {
    cantidad,
    costoUnitarioSuelto: uno.costoTotal,
    costoUnitarioEnLote: lote.costoTotal,
    bajaPct: baja,
    placasSueltas: uno.materiales.reduce((a, m) => a + Math.ceil(m.placasPedido), 0) * cantidad,
    placasEnLote: lote.materiales.reduce((a, m) => a + Math.ceil(m.placasPedido), 0)
  }
}

/** Un producto tiene despiece si alguien se lo capturó. */
export const tieneDespiece = d => !!(d && (d.piezas?.length || d.herrajes?.length || d.procesos?.length))
